export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0A0A0A]">
      {/* Background image */}
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-center scale-105"
        style={{
          backgroundImage: "url('https://public.readdy.ai/ai/img_res/2b19682b7e13b646dbe4e0a74535f2e5.jpg')",
        }}
      />
      {/* Heavy dark overlays for readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/95 via-[#0A0A0A]/85 to-[#0A0A0A]/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-[#050505]/40" />

      {/* Orange left accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[#FF6B35] to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-40 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* Left — 7 cols */}
          <div className="lg:col-span-7">
            {/* Label badges */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="inline-flex items-center gap-2 border border-[#FF6B35]/50 bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full">
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-award-fill text-xs" /></span>
                XPEL Certified Installer
              </span>
              <span className="inline-flex items-center gap-2 border border-white/15 text-white/60 text-xs font-semibold px-4 py-2 rounded-full">
                South Windsor, CT
              </span>
            </div>

            {/* Headline — large, readable, high contrast */}
            <h1 className="font-display font-black leading-[1.02] mb-6">
              <span className="block text-white" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>
                South Windsor's Premier
              </span>
              <span className="block text-[#FF6B35]" style={{ fontSize: 'clamp(2.8rem, 5.5vw, 5rem)' }}>
                Auto Protection
              </span>
              <span className="block text-white" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>
                &amp; Enhancement Studio
              </span>
            </h1>

            {/* Sub */}
            <p className="text-white/80 text-lg leading-relaxed mb-2 max-w-xl">
              PPF · Ceramic Coating · Window Tint · Matte Wraps · Windshield Protection
            </p>
            <p className="text-white/50 text-base mb-10 max-w-lg">
              25+ years of automotive passion. Precision-installed premium protection for the vehicles you love most.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a href="#lead-form"
                className="flex items-center justify-center gap-3 bg-[#FF6B35] hover:bg-[#E5501A] text-white font-black text-base px-9 py-4 rounded-xl transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
                style={{ boxShadow: '0 10px 30px rgba(255,107,53,0.35)' }}>
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-calendar-check-fill text-lg" /></span>
                Book Free Consultation
              </a>
              <a href="tel:+18605550127"
                className="flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/70 hover:bg-white/10 text-white font-bold text-base px-8 py-4 rounded-xl transition-all whitespace-nowrap cursor-pointer">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill" /></span>
                (860) 555-0127
              </a>
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {[
                { icon: 'ri-award-fill', text: 'XPEL Certified' },
                { icon: 'ri-shield-star-fill', text: '10-Year PPF Warranty' },
                { icon: 'ri-car-fill', text: '500+ Vehicles Protected' },
                { icon: 'ri-star-fill', text: '5★ Google Rated' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center"><i className={`${b.icon} text-[#FF6B35] text-sm`} /></span>
                  <span className="text-white/65 text-sm font-medium whitespace-nowrap">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — proof card (5 cols) */}
          <div className="lg:col-span-5 hidden lg:flex flex-col gap-4">
            {/* Review card */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-[#FF6B35] text-sm" /></span>
                ))}
                <span className="ml-2 text-white/40 text-xs">Google Review</span>
              </div>
              <p className="text-white/80 text-sm italic leading-relaxed">
                &ldquo;Full PPF on my BMW M3. Absolutely flawless install. DBN Garage is the real deal — best shop in Connecticut.&rdquo;
              </p>
              <p className="text-white/40 text-xs font-semibold mt-3">— Marcus T., South Windsor CT</p>
            </div>

            {/* Services quick pills */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: 'ri-shield-fill', label: 'Paint Protection Film' },
                { icon: 'ri-sparkling-2-fill', label: 'Ceramic Coating' },
                { icon: 'ri-sun-fill', label: 'Window Tint' },
                { icon: 'ri-paint-fill', label: 'Matte PPF' },
              ].map((s) => (
                <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center bg-[#FF6B35]/15 rounded-lg flex-shrink-0">
                    <i className={`${s.icon} text-[#FF6B35] text-base`} />
                  </span>
                  <span className="text-white/70 text-xs font-semibold leading-tight">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/25 animate-bounce">
        <span className="text-xs uppercase tracking-[0.2em]">Explore</span>
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-down-line" /></span>
      </div>
    </section>
  );
}
